import { CURRENCIES } from "@kupikupi/brand";

export type Locale = "ru-RU" | "zh-CN" | "en-US";
export type Currency = "RUB" | "CNY" | "USD";

const KNOWN_CURRENCIES = new Set<Currency>(["RUB", "CNY", "USD"]);

export function normalizeLocale(locale: string | undefined): Locale {
  if (locale === "zh" || locale?.startsWith("zh")) return "zh-CN";
  if (locale === "en" || locale?.startsWith("en")) return "en-US";
  return "ru-RU";
}

function asCurrency(code: string | undefined): Currency | null {
  return code && KNOWN_CURRENCIES.has(code as Currency) ? (code as Currency) : null;
}

export function formatPrice(kopecks: number, locale: Locale, currency: Currency = "RUB"): string {
  const maximumFractionDigits = currency === "RUB" ? 0 : 2;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(kopecks / 100);
}

/**
 * The currency a given UI locale prefers to *display first* (#41).
 * Amounts are still stored as integer RUB kopecks (+ derived CNY fen) — this
 * only decides which side of the dual display is the primary one.
 *   zh-CN → CNY · ru-RU / en-US → RUB
 */
export function localeToCurrency(locale: Locale | string | undefined): Currency {
  return normalizeLocale(locale) === "zh-CN" ? "CNY" : "RUB";
}

/**
 * Locale-driven display/request currency, constrained by the env whitelist.
 * Staging/dev can map zh→CNY; prod RUB-only explicitly falls back to RUB.
 */
export function displayCurrencyForLocale(
  locale: Locale | string | undefined,
  currencies: readonly string[] = CURRENCIES,
): Currency {
  const preferred = localeToCurrency(locale);
  if (currencies.includes(preferred)) return preferred;
  return (
    currencies.map(asCurrency).find((currency): currency is Currency => currency != null) ?? "RUB"
  );
}

/**
 * Format the RUB/CNY dual display, choosing which currency is primary from the
 * locale (#41) and honouring the env currency whitelist.
 *
 * Storage is unchanged: `rubKopecks` is always integer RUB kopecks and
 * `cnyFen` is the rate-derived integer CNY fen. We never mutate the amounts —
 * we only pick which one renders large (primary) vs. muted (secondary).
 *
 *   - locale wants CNY + CNY whitelisted + cnyFen present → CNY primary, RUB secondary
 *   - locale wants RUB (or CNY unavailable) → RUB primary, CNY secondary (when whitelisted + present)
 *   - single-currency whitelist (e.g. prod RUB-only) → that currency primary, no secondary
 */
export function formatDualFromList(
  rubKopecks: number,
  locale: Locale,
  cnyFen: number | undefined,
  currencies: readonly string[],
): { primary: string; secondary: string | null } {
  const hasRub = currencies.includes("RUB");
  const hasCny = currencies.includes("CNY") && cnyFen !== undefined;

  // Which currency does this locale want up front, if it's actually available?
  const wantsCny = displayCurrencyForLocale(locale, currencies) === "CNY";
  const primaryCurrency: Currency = wantsCny && hasCny ? "CNY" : hasRub ? "RUB" : "CNY";

  if (primaryCurrency === "CNY") {
    const primary = formatPrice(cnyFen as number, locale, "CNY");
    const secondary =
      hasRub && currencies.length > 1 ? `≈ ${formatPrice(rubKopecks, locale, "RUB")}` : null;
    return { primary, secondary };
  }

  const primary = formatPrice(rubKopecks, locale, "RUB");
  const secondary =
    hasCny && currencies.length > 1 ? `≈ ${formatPrice(cnyFen as number, locale, "CNY")}` : null;
  return { primary, secondary };
}

export function formatDual(rubKopecks: number, locale: Locale, cnyFen?: number) {
  return formatDualFromList(rubKopecks, locale, cnyFen, CURRENCIES);
}
